import React from 'react';

interface BarData {
  label: string;
  value2023: number;
  value2024: number;
}

export default function HistoricalComputeBreakdown() {
  // Chart dimensions
  const width = 800;
  const height = 600;
  const margin = { top: 60, right: 40, bottom: 60, left: 180 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;
  
  // Data for the bars in H100e (thousands)
  const data: BarData[] = [
    { 
      label: "Microsoft", 
      value2023: 300,
      value2024: 1100
    },
    { 
      label: "Google", 
      value2023: 800,
      value2024: 1800
    },
    { 
      label: "Amazon", 
      value2023: 600,
      value2024: 1300
    },
    { 
      label: "Meta", 
      value2023: 450,
      value2024: 1000
    },
    { 
      label: "xAI", 
      value2023: 10,
      value2024: 200
    },
    { 
      label: "Rest of the US", 
      value2023: 500,
      value2024: 1400
    },
    { 
      label: "China Big Four", 
      value2023: 300,
      value2024: 800
    },
    { 
      label: "Rest of the world", 
      value2023: 450,
      value2024: 800
    }
  ];
  
  // Find maximum value for scaling
  const maxValue = Math.max(
    ...data.map(d => Math.max(d.value2023, d.value2024))
  );
  
  // Round up to nearest 500K for nice axis
  const axisMax = 2000; // 2M as shown in the image
  
  // X-axis ticks
  const xTicks = [0, 500, 1000, 1500, 2000];
  
  // Bar height and spacing
  const barHeight = 18;
  const barSpacing = chartHeight / data.length;
  
  // Scale for x-axis
  const xScale = (value: number) => (value / axisMax) * chartWidth;
  
  // Format numbers with K for thousands
  const formatValue = (value: number) => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}M`;
    }
    return `${value}K`;
  };
  
  return (
    <div className="w-full relative overflow-hidden mt-8 mb-8">
      <svg
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        viewBox={`0 0 ${width} ${height}`}
      >
        {/* Chart Title */}
        <text
          x={margin.left}
          y={30}
          fontSize="22"
          fontFamily="monospace"
          className="text-[22px] font-mono md:text-[20px] sm:text-[18px]"
          fill="#666666"
        >
          Historical breakdown by compute owners
        </text>
        
        {/* Subtitle */}
        <text
          x={margin.left}
          y={55}
          fontSize="16"
          fontFamily="monospace"
          className="text-[16px] font-mono italic md:text-[14px] sm:text-[12px]"
          fill="#888888"
          fontStyle="italic"
        >
          Estimated, H100e
        </text>
        
        {/* Vertical grid lines */}
        {xTicks.map((tick) => (
          <g key={`tick-${tick}`}>
            <line
              x1={margin.left + xScale(tick)}
              y1={margin.top}
              x2={margin.left + xScale(tick)}
              y2={margin.top + chartHeight}
              stroke="#e0e0e0"
              strokeWidth="1"
            />
            <text
              x={margin.left + xScale(tick)}
              y={margin.top + chartHeight + 20}
              textAnchor="middle"
              fontSize={14}
              fontFamily="monospace"
              fill="#666666"
            >
              {tick > 0 ? `${tick}K` : '0K'}
            </text>
          </g>
        ))}
        
        {/* Y-axis labels (Company names) */}
        {data.map((d, i) => (
          <g key={`label-${i}`}>
            <text
              x={margin.left - 10}
              y={margin.top + i * barSpacing + barSpacing / 2 + 5}
              textAnchor="end"
              fontSize={14}
              fontFamily="monospace"
              fontWeight="bold"
              fill="#000000"
            >
              {d.label}
            </text>
          </g>
        ))}
        
        {/* 2023 Bars (Blue) */}
        {data.map((d, i) => (
          <g key={`bar2023-${i}`}>
            <rect
              x={margin.left}
              y={margin.top + i * barSpacing + (barSpacing - barHeight * 2 - 5) / 2}
              width={xScale(d.value2023)}
              height={barHeight}
              fill="#333333"
              rx={2}
              ry={2}
            />
            <text
              x={margin.left + xScale(d.value2023) + 5}
              y={margin.top + i * barSpacing + (barSpacing - barHeight * 2 - 5) / 2 + barHeight / 2 + 4}
              textAnchor="start"
              fontSize={14}
              fontFamily="monospace"
              fill="#333333"
            >
              {formatValue(d.value2023)}
            </text>
          </g>
        ))}
        
        {/* 2024 Bars (Red) */}
        {data.map((d, i) => (
          <g key={`bar2024-${i}`}>
            <rect
              x={margin.left}
              y={margin.top + i * barSpacing + (barSpacing - barHeight * 2 - 5) / 2 + barHeight + 5}
              width={xScale(d.value2024)}
              height={barHeight}
              fill="var(--accent, #006000)"
              rx={2}
              ry={2}
            />
            <text
              x={margin.left + xScale(d.value2024) + 5}
              y={margin.top + i * barSpacing + (barSpacing - barHeight * 2 - 5) / 2 + (barHeight + 5) + barHeight/2+4}
              textAnchor="start"
              fontSize={14}
              fontFamily="monospace"
              fill="#333333"
            >
              {formatValue(d.value2024)}
            </text>
          </g>
        ))}
        
        {/* Legend */}
        <g transform={`translate(${margin.left}, ${margin.top + chartHeight + 40})`}>
          {/* 2023 Legend Item */}
          <rect
            x={0}
            y={0}
            width={15}
            height={15}
            fill="#333333"
            rx={2}
            ry={2}
          />
          <text
            x={20}
            y={12}
            fontSize={14}
            fontFamily="monospace"
            fill="#333333"
          >
            December 2023
          </text>
          
          {/* 2024 Legend Item */}
          <rect
            x={160}
            y={0}
            width={15}
            height={15}
            fill="#006000"
            rx={2}
            ry={2}
          />
          <text
            x={180}
            y={12}
            fontSize={14}
            fontFamily="monospace"
            fill="#333333"
          >
            December 2024
          </text>
        </g>
      </svg>
    </div>
  );
}